import { useState, useEffect } from "react";
import apiService from "../services/apiService";
import { getLocalStorageItem } from "../utils/Validation.ts";

interface StatusResponse {
  isActive: boolean;
  lastActive?: string;
  // Add other fields from the response as needed
}

/**
 * Custom hook to check user active status every 10 seconds
 * @returns Object containing the active status response
 */
const useActiveStatusCheck = () => {
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const checkStatus = async () => {
      const token = getLocalStorageItem("authToken");
      if (!token || typeof token !== "string" || token.trim() === "") {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await apiService.get("/users/check-active-status");
        setStatus(response as StatusResponse);
        setError(null);
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Unknown error occurred")
        );
        console.error("Error checking active status:", err);
      } finally {
        setLoading(false);
      }
    };

    // Initial check
    checkStatus();

    // Set up interval for periodic checks
    const intervalId = setInterval(checkStatus, 20000);

    // Clean up function to clear interval when component unmounts
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  return { status, loading, error };
};

export default useActiveStatusCheck;
